<template>
  <form ref="form" class="form-signup-v2" @submit="submit">
    <app-overlay :show="busy" variant="dark">
      <div class="row g-2">
        <div class="username-field col-12">
          <input
            v-model="username"
            type="text"
            class="form-control"
            :class="getFieldState('username') ? `is-${getFieldState('username')}` : ''"
            :placeholder="t('Username')"
            required
            @input="onChangeUsername"
          />
          <div class="invalid-feedback">
            {{ validationErrors["username"] }}
          </div>
        </div>

        <div class="email-field col-12">
          <input
            v-model="email"
            type="email"
            class="form-control"
            :class="getFieldState('email') ? `is-${getFieldState('email')}` : ''"
            :placeholder="t('Email')"
            required
            @input="onChangeEmail"
          />
          <div class="invalid-feedback">
            {{ validationErrors["email"] }}
          </div>
        </div>

        <div class="voucher-field col-12">
          <input
            v-model="voucher"
            type="text"
            class="form-control"
            :class="getFieldState('voucher') ? `is-${getFieldState('voucher')}` : ''"
            :placeholder="t('Voucher Code (optional)')"
          />
          <div class="invalid-feedback">
            {{ validationErrors["voucher"] }}
          </div>
        </div>

        <div class="col-12">
          <captcha
            v-if="showCaptcha"
            ref="captchaRef"
            :error="validationErrors['captcha']"
            @on-captcha-verify="onCaptchaVerify"
            @on-captcha-expired="onCaptchaExpired"
          />
        </div>
      </div>

      <button class="btn btn-primary border-dark mt-4 w-100" type="submit" @click="submit">
        {{ t("Create an account") }}
      </button>
    </app-overlay>
  </form>
</template>

<script setup lang="ts">
import { computed, onMounted, Ref, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import Captcha from "@/components/captcha";
import securityService from "@/api/services/securityService";
import AppOverlay from "@/components/v2/app-overlay";
import freeareaService from "@/api/services/freeareaService";
import { setAccessToken } from "@/utils/storage";
import userService from "@/api/services/userService";
import { amplitudeTrack } from "@/utils/amplitude";
import { SignupParams } from "@/interfaces/Freearea/SignupParams.ts";
import { useUserStore } from "@/store/user.ts";

const props = defineProps({
  redirectUrl: {
    type: String,
    required: false,
    default: null,
  },
});

const userStore = useUserStore();
const router = useRouter();
const route = useRoute();
const { t, locale } = useI18n();

const form = ref<HTMLFormElement | null>(null);
const captchaRef = ref<any>(null);
const validationErrors = ref<Record<string, any>>({
  username: null,
  email: null,
  captcha: null,
});
const validationTimers = ref<Record<string, number>>({});
const showCaptcha = ref(false);
const username = ref("");
const email = ref("");
const voucher = ref("");
const captchaToken = ref("");
const busy = ref(false);
const emailChangeTracked = ref(false);

const landingUrl = computed(() => userStore.getLandingUrl);

const landingId = ref("");

watch(
  () => validationErrors.value,
  async (newVal: Record<string, any>) => {
    if (newVal.captcha) {
      showCaptcha.value = true;
      captchaToken.value = "";
      captchaRef.value.reset();
    }
  },
  { deep: true },
);

const getFieldState = (field: string) => {
  if (validationErrors.value[field] === false) {
    return "valid";
  }

  return validationErrors.value[field] ? "invalid" : null;
};

const validateField = async (fieldName: string, field: Ref<string>, validator: () => Promise<any>) => {
  clearTimeout(validationTimers.value[fieldName]);

  if (!field.value) {
    validationErrors.value[fieldName] = null;
    return;
  }

  validationTimers.value[fieldName] = window.setTimeout(async () => {
    try {
      await validator();
      validationErrors.value[fieldName] = false;
    } catch (e: any) {
      validationErrors.value[fieldName] = e.status?.message[locale.value] || t("Something went wrong");
    }
  }, 500);
};

const onChangeUsername = () => {
  validateField("username", username, () => {
    return userService.validateUsername(username.value);
  });
};

const onChangeEmail = () => {
  if (!emailChangeTracked.value && email.value) {
    amplitudeTrack("Signup popup email change");
    emailChangeTracked.value = true;
  }

  validateField("email", email, () => {
    return userService.validateEmail(email.value);
  });
};

const onCaptchaVerify = (token: string) => {
  captchaToken.value = token;
};

const onCaptchaExpired = () => {
  captchaToken.value = "";
};

const submit = async (e: Event) => {
  e.preventDefault();

  if (busy.value) {
    return;
  }

  busy.value = true;

  validationErrors.value = {
    username: null,
    email: null,
    captcha: null,
  };

  if (showCaptcha.value && captchaToken.value === null) {
    amplitudeTrack("Signup popup captcha failed");

    validationErrors.value.captcha = t("Security check failed");
    return;
  }

  amplitudeTrack("Signup popup click 'submit'");

  const params: SignupParams = {
    username: username.value,
    email: email.value,
    language: locale.value,
    captcha: captchaToken.value,
  };

  const landingId = route.params?.id || route.meta?.landing_id;

  if (landingId) {
    params.landing_id = landingId as string;
  }

  if (voucher.value) {
    params.voucher_code = voucher.value as string;
  }

  try {
    const response = await freeareaService.signup(params);

    setAccessToken(response.access_token);

    const redirectUrl = props.redirectUrl ? props.redirectUrl : `/${(response?.redirect_url || "").replace(/^\/|\/$/g, "")}`;
    await router.push(redirectUrl);
  } catch (e: any) {
    if (e.status?.status === 403) {
      await router.push(landingUrl.value);
      return;
    }

    if (e.status?.type) {
      const field = e.status.type.replace("INVALID_", "").toLowerCase();
      validationErrors.value[field] = e.status?.message[locale.value] || t("Something went wrong");
    } else {
      throw e;
    }
  } finally {
    busy.value = false;
  }
};

onMounted(async () => {
  landingId.value = (route.params?.id || route.meta?.landing_id) as string;
  showCaptcha.value = await securityService.getCaptcha();
});
</script>
